import { useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  Boxes,
  ChevronDown,
  Link2,
  LogOut,
  Search,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  TailCampaignIcon,
  TailClipboardIcon,
  TailDashboardIcon,
  TailMessageIcon,
  TailPackageIcon,
  TailSettingsIcon,
  TailUsersIcon,
} from "@/components/template/tailadmin/TailAdminIcons";

const navGroups = [
  {
    title: "Menu",
    items: [
      { path: "/client/dashboard", icon: TailDashboardIcon, label: "Dashboard" },
      { path: "/client/projects", icon: Boxes, label: "Projects" },
      {
        path: "/client/products",
        icon: TailPackageIcon,
        label: "Offers & campaigns",
        children: [
          { path: "/client/products", label: "Your offers" },
          { path: "/client/campaigns", label: "All campaigns" },
          { path: "/client/prove-requests/create", label: "New offer" },
          { path: "/client/campaigns/create", label: "New campaign for an offer" },
        ],
      },
      { path: "/client/packages", icon: Zap, label: "Growth Plans" },
      { path: "/client/brms", icon: TailUsersIcon, label: "Agents" },
      { path: "/client/enrollments", icon: TailClipboardIcon, label: "Enrollments" },
    ],
  },
  {
    title: "Operations",
    items: [
      { path: "/client/messages", icon: TailMessageIcon, label: "Messages" },
      { path: "/client/settings", icon: TailSettingsIcon, label: "Settings" },
    ],
  },
];

const allNavItems = navGroups.flatMap((group) => group.items);

const ClientLayout = ({ children, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800">
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden w-[290px] border-r border-gray-200 bg-white px-5 xl:block"
        data-testid="client-sidebar"
      >
        <div className="flex h-20 items-center">
          <button onClick={() => navigate("/client/dashboard")} className="flex items-center gap-3 text-left">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500 text-white shadow-theme-xs">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-lg font-semibold text-gray-900">Proovam</span>
              <span className="block text-xs text-gray-500">Client workspace</span>
            </span>
          </button>
        </div>

        <nav className="no-scrollbar h-[calc(100vh-160px)] overflow-y-auto pb-8">
          {navGroups.map((group) => (
            <div key={group.title} className="mb-6">
              <h2 className="mb-4 text-xs font-medium uppercase leading-5 text-gray-400">{group.title}</h2>
              <ul className="flex flex-col gap-1">
                {group.items.map((item) => (
                  <li key={item.path}>
                    <button
                      onClick={() => navigate(item.path)}
                      className={cn(
                        "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition",
                        isActive(item.path)
                          ? "bg-brand-50 text-brand-500"
                          : "text-gray-700 hover:bg-gray-100 hover:text-gray-800"
                      )}
                      data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <item.icon className="h-6 w-6" />
                      <span>{item.label}</span>
                      {item.children ? <ChevronDown className="ml-auto h-4 w-4" /> : null}
                    </button>
                    {item.children ? (
                      <ul className="ml-9 mt-1 space-y-1">
                        {item.children.map((child) => (
                          <li key={child.path}>
                            <button
                              onClick={() => navigate(child.path)}
                              className={cn(
                                "flex w-full rounded-lg px-3 py-2 text-left text-sm transition",
                                isActive(child.path)
                                  ? "bg-brand-50 text-brand-500"
                                  : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                              )}
                            >
                              {child.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-5">
          <Button
            variant="ghost"
            className="h-11 w-full justify-start text-gray-500 hover:bg-rose-50 hover:text-rose-600"
            onClick={onLogout}
            data-testid="sidebar-logout"
          >
            <LogOut className="mr-2 h-5 w-5" />
            Logout
          </Button>
        </div>
      </aside>

      <div className="xl:pl-[290px]">
        <header className="sticky top-0 z-30 flex min-h-16 flex-col border-b border-gray-200 bg-white/95 px-4 backdrop-blur md:px-6 xl:h-16 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex h-16 items-center justify-between gap-3">
            <button className="flex items-center gap-2 xl:hidden" onClick={() => navigate("/client/dashboard")}>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-500 text-white">
                <Zap className="h-4 w-4" />
              </span>
              <span className="font-semibold text-gray-900">Proovam</span>
            </button>

            <label className="relative hidden md:block">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                className="h-11 w-[360px] rounded-lg border border-gray-200 bg-transparent py-2 pl-11 pr-4 text-sm outline-none placeholder:text-gray-400 focus:border-brand-300"
                placeholder="Search campaigns, Agents, enrollments..."
              />
            </label>

            <div className="flex items-center gap-2">
              <button className="rounded-full border border-gray-200 p-2.5 text-gray-500 hover:bg-gray-50">
                <Bell className="h-5 w-5" />
              </button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-rose-600 xl:hidden"
                onClick={onLogout}
                data-testid="mobile-logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <nav className="flex items-center gap-1 overflow-x-auto pb-3 xl:hidden">
            {allNavItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={cn(
                  "flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition",
                  isActive(item.path)
                    ? "bg-brand-50 font-medium text-brand-500"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                )}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="hidden items-center gap-3 xl:flex">
            <button
              onClick={() => navigate("/client/products")}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Link2 className="h-4 w-4 text-brand-500" />
              Offers & campaigns
            </button>
            <div className="flex items-center gap-3 rounded-full border border-gray-200 py-1 pl-1 pr-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-50 text-brand-500">
                <Boxes className="h-4 w-4" />
              </div>
              <span className="text-sm font-medium text-gray-700">Client Admin</span>
            </div>
          </div>
        </header>

        <main className="min-h-[calc(100vh-64px)] pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="mx-auto max-w-screen-2xl p-4 pb-8 md:p-6 md:pb-10">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default ClientLayout;
